import {
	ChangeDetectionStrategy, Component, forwardRef, Input, Type, ChangeDetectorRef, OnInit, OnDestroy
} from "@angular/core";
import {
	AbstractControl,
	ControlContainer,
	NG_VALUE_ACCESSOR
} from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { BoxValueAccessor } from "@shared/box-value-accessor/BoxValueAccessor";

@Component({
	selector: "app-textbox",
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		multi: true,
		useExisting: forwardRef((): Type<TextboxComponent> => TextboxComponent)
	}],
	template: `
        <input
			#input
            type="text"
			[value]="value"
			(input)="onInput(input.value)"
			placeholder="{{ placeholder }}"
			[attr.maxlength]="maxLength"
            [attr.disabled]="disabled"
			[class.error]="showError"
			[attr.title]="showTitle ? value : ''"
			[class.with-icon]="icon"
			[autocomplete]="autocomplete">
				<app-icon *ngIf="icon">{{ icon }}</app-icon>
	`,
	styleUrls: ["./textbox.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class TextboxComponent extends BoxValueAccessor implements OnInit, OnDestroy {
	public value?: string | null = "";
	@Input() public placeholder?: string;
	@Input() public icon?: string;
	@Input() public noTrimValue = false;
	@Input() public isMarkinCode = false;

	@Input()
	public set withTitle(value: boolean | "") {
		this.showTitle = value || value === "";
	}

	@Input()
	public set isDisabled(value: boolean) {
		this.disabled = value ? "" : null;
	}

	@Input()
	public set hideError(value: boolean) {
		this.showError = !value;
	}

	@Input() public maxLength?: number;
	@Input() public disableAutoCheck = false;
	@Input() public formControlName?: string;
	@Input() public displayValidation = true;
	@Input() public error = false;
	@Input() public formControl?: AbstractControl;
	@Input() public autocomplete?: string = "on";

	public disabled: "" | null = null;
	public showTitle = false;

	public showError = false;
	public control?: AbstractControl | null;

	public unsubscribe$$: Subject<void> = new Subject<void>();

	constructor(
		public readonly cdr: ChangeDetectorRef,
		public readonly controlContainer: ControlContainer
	) {
		super();
	}

	public ngOnInit(): void {
		if (!this.displayValidation)
			return;

		if (this.formControl)
			this.control = this.formControl;

		if (this.controlContainer && this.formControlName && this.controlContainer.control)
			this.control = this.controlContainer.control.get(this.formControlName);

		// Subscribe need to change control touched state
		this.control && this.control.statusChanges.pipe(takeUntil(this.unsubscribe$$)).subscribe(() => {
			if (!this.error)
				this.showError = this.control && this.control.touched && this.control.invalid || false;
			else
				this.showError = this.error;
			this.cdr.detectChanges();
		});
	}

	public writeValue(value: string | null, isFloatInput = false): void {
		this.value = this.checkValue(value && typeof value === "string" && !this.noTrimValue ? isFloatInput ? value.replace(",", ".") : value.trim() : value);
		this.cdr.markForCheck();
	}

	public onInput(newValue: string): void {
		const clearInputValue = !this.isMarkinCode ? newValue.replace(/\u001D/gm, "") : newValue;
		const trimValue = clearInputValue && typeof clearInputValue === "string" && !this.noTrimValue ? clearInputValue.trim() : clearInputValue;
		const checkedValue = this.checkValue(trimValue);
		this.writeValue(checkedValue);
		if (this.onChange)
			this.onChange(checkedValue);
		if (this.onTouched)
			this.onTouched();
	}

	public setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
		this.cdr.markForCheck();
	}

	public ngOnDestroy(): void {
		this.unsubscribe$$.next();
		this.unsubscribe$$.complete();
	}

	public checkValue(value: string | null): string | null {
		const isEmptyString = typeof value === "string" && value === "";
		return isEmptyString ? null : value;
	}
}
